import {
  billingMethodEnum,
  customerMembers,
  customers,
  customerStatusEnum,
  customerTypeEnum,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { emailSchema } from './common'

// Base schemas. internal* fields are stripped (set to undefined) by
// omitInternalFields for non-staff roles, so they must be optional on the
// response schema for Zod validation to pass after the strip.
export const selectCustomerSchema = createSelectSchema(customers, {
  internalFirstTouchAcquisition: z.unknown().optional(),
})
export const insertCustomerSchema = createInsertSchema(customers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateCustomerSchema = insertCustomerSchema.partial()

// Billing emails are stored lowercased so bounce processing and Stripe sync
// compare them reliably. '' (cleared form field) maps to null; undefined
// stays undefined so partial updates leave the field untouched.
export const billingEmailSchema = z
  .union([emailSchema, z.literal('')])
  .nullable()
  .optional()
  .transform((val) => (val === '' ? null : val))

// Customer-only fields used by the bare CRUD upsert (create + update).
// Members are managed separately via addMember/removeMember on the detail
// page after the customer is created — keeping create simple sidesteps the
// duplicate-user-via-customer-create question entirely.
export const upsertCustomerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(customerTypeEnum.enumValues).default('household'),
  status: z.enum(customerStatusEnum.enumValues).default('active'),
  billingMethod: z.enum(billingMethodEnum.enumValues).default('email'),
  billingEmail: billingEmailSchema,
  notes: z.string().optional(),
  // Finnish Y-tunnus. Nullable; uniqueness among non-deleted rows is enforced
  // by the partial unique index, not here.
  businessId: z.string().nullable().optional(),
  // Office / building address of the organization.
  addressId: z.number().nullable().optional(),
  // Managing agency for a housing company. Validated server-side in the
  // customer router upsert transform (housing_company -> agency, not self).
  managedByCustomerId: z.number().nullable().optional(),
  // Konserni parent for an agency. Validated server-side in the same
  // transform (agency -> agency, not self).
  parentCustomerId: z.number().nullable().optional(),
  // Key account manager (officer/service_manager/admin user). Set by staff;
  // independent per customer (no inheritance from a managing agency).
  accountManagerUserId: z.number().nullable().optional(),
})

// Legacy schema kept for any callers that still pass nested `user` data
// (e.g. tests, `property.createWithNewCustomer` adapters). Do not use for
// the bare CRUD endpoint — see upsertCustomerSchema.
export const createCustomerWithUserSchema = z.object({
  // Customer fields
  name: z.string().min(1, 'Name is required'),
  type: z.enum(customerTypeEnum.enumValues).default('household'),
  status: z.enum(customerStatusEnum.enumValues).default('active'),
  billingMethod: z.enum(billingMethodEnum.enumValues).default('email'),
  billingEmail: billingEmailSchema,
  notes: z.string().optional(),
  // Primary user fields
  user: z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: emailSchema,
    phoneNumber: z.string().optional(),
  }),
})

// Customer member schemas
export const selectCustomerMemberSchema = createSelectSchema(customerMembers)
export const insertCustomerMemberSchema = createInsertSchema(
  customerMembers,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const customerPropertySchema = z.object({
  id: z.number(),
  propertyType: z.string(),
  constructionYear: z.number().nullable(),
  notes: z.string().nullable(),
  address: z
    .object({
      street: z.string(),
      city: z.string(),
      postalCode: z.string().nullable(),
      latitude: z.string().nullable(),
      longitude: z.string().nullable(),
    })
    .nullable(),
})

export const customerPropertyWithSubscriptionsSchema =
  customerPropertySchema.extend({
    subscriptions: z
      .array(
        z.object({
          id: z.number(),
          status: z.string(),
          startDate: z.string(),
          endDate: z.string().nullable().optional(),
          displayName: z.string().nullable().optional(),
          basePriceMonthly: z.string().nullable().optional(),
          effectivePriceMonthly: z.string().nullable().optional(),
          additionalTerms: z.string().nullable().optional(),
          subscriptionPackageVersions: z
            .array(
              z.object({
                id: z.number(),
                quantity: z.number(),
                sortOrder: z.number(),
                servicePackageVersion: z.object({
                  id: z.number(),
                  versionNumber: z.number(),
                  priceMonthly: z.string(),
                }),
              }),
            )
            .optional(),
        }),
      )
      .optional(),
  })

// Customer with relations
export const selectCustomerWithRelationsSchema = selectCustomerSchema.extend({
  members: z
    .array(
      selectCustomerMemberSchema.extend({
        user: z.object({
          id: z.number(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string().nullable(),
          phoneNumber: z.string().nullable(),
          emailVerifiedAt: z.string().nullable().optional(),
        }),
      }),
    )
    .optional(),
  properties: z.array(customerPropertyWithSubscriptionsSchema).optional(),
  // The managing agency for a housing_company customer. Self-FK on
  // customers.managedByCustomerId. Null for non-housing-companies and for
  // taloyhtiöt with no agency on record.
  managedByAgency: z
    .object({
      id: z.number(),
      name: z.string(),
      type: z.enum(customerTypeEnum.enumValues),
    })
    .nullable()
    .optional(),
  // The key account manager assigned to this customer. Contact fields only —
  // never the full user object (avoids leaking internal user fields).
  accountManager: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().nullable(),
      phoneNumber: z.string().nullable(),
    })
    .nullable()
    .optional(),
})

// Type exports
export type Customer = z.infer<typeof selectCustomerSchema>
export type CustomerWithRelations = z.infer<
  typeof selectCustomerWithRelationsSchema
>
export type CreateCustomerWithUser = z.infer<
  typeof createCustomerWithUserSchema
>
export type UpsertCustomer = z.infer<typeof upsertCustomerSchema>
export type UpdateCustomer = z.infer<typeof updateCustomerSchema>
export type CustomerMember = z.infer<typeof selectCustomerMemberSchema>
